import { ArrowRight, Users, MapPin, Package, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import villageBullockCart from "@/assets/village-bullock-cart.webp";
import { useSiteContent, getContentValue } from "@/hooks/useSiteContent";
import { OptimizedImage } from "@/components/ui/optimized-image";
import AnimatedCounter from "@/components/AnimatedCounter";

const Hero = () => {
  const { data: heroContent } = useSiteContent("hero_content");
  
  const content = heroContent?.value as Record<string, string> | undefined;
  
  const tagline = getContentValue(content, "tagline", "Living Traditions from Rural India");
  const title = getContentValue(content, "title", "Crafts that Carry");
  const titleHighlight = getContentValue(content, "title_highlight", "Centuries");
  const description = getContentValue(content, "description", "Every piece is shaped by hands that have practiced their craft for generations — Madhubani painters, Dhokra casters, village potters. Listen first. Buy if it speaks to you.");
  
  const stat1Value = getContentValue(content, "stat1_value", "500+");
  const stat1Label = getContentValue(content, "stat1_label", "Artisans");
  const stat2Value = getContentValue(content, "stat2_value", "50+");
  const stat2Label = getContentValue(content, "stat2_label", "Villages");
  const stat3Value = getContentValue(content, "stat3_value", "10K+");
  const stat3Label = getContentValue(content, "stat3_label", "Products");

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <OptimizedImage
          src={villageBullockCart}
          alt="Traditional Indian village scene with bullock cart"
          className="w-full h-full"
          objectFit="cover"
          priority
        />
        <div className="absolute inset-0 gradient-hero" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-golden/20 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-terracotta/20 rounded-full blur-3xl animate-float" style={{ animationDelay: "1s" }} />

      {/* Content */}
      <div className="container relative z-10 pt-20 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-cream/20 backdrop-blur-sm rounded-full text-cream text-sm font-medium mb-6 animate-fade-in">
            <Heart className="h-4 w-4 text-terracotta fill-terracotta" />
            {tagline}
          </span>
          
          <h1 className="font-display text-4xl md:text-5xl lg:text-7xl font-bold text-cream mb-6 leading-tight animate-slide-up">
            {title}{" "}
            <span className="text-golden-light">{titleHighlight}</span>
          </h1>
          
          <p className="text-lg md:text-xl text-cream/90 mb-8 max-w-2xl mx-auto leading-relaxed animate-slide-up" style={{ animationDelay: "0.2s" }}>
            {description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: "0.4s" }}>
            <Button variant="hero" size="xl" className="w-full sm:w-auto group" asChild>
              <Link to="/artisans">
                Meet the Makers
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button variant="ghost" size="xl" className="w-full sm:w-auto text-cream border-cream/30 border hover:bg-cream/10" asChild>
              <Link to="/story">Our Story</Link>
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 pt-8 border-t border-cream/20 animate-fade-in" style={{ animationDelay: "0.6s" }}>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Users className="h-5 w-5 text-golden-light" />
                <p className="font-display text-3xl md:text-4xl font-bold text-golden-light"><AnimatedCounter value={stat1Value} /></p>
              </div>
              <p className="text-cream/70 text-sm">{stat1Label}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <MapPin className="h-5 w-5 text-golden-light" />
                <p className="font-display text-3xl md:text-4xl font-bold text-golden-light"><AnimatedCounter value={stat2Value} duration={1800} /></p>
              </div>
              <p className="text-cream/70 text-sm">{stat2Label}</p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Package className="h-5 w-5 text-golden-light" />
                <p className="font-display text-3xl md:text-4xl font-bold text-golden-light"><AnimatedCounter value={stat3Value} duration={2200} /></p>
              </div>
              <p className="text-cream/70 text-sm">{stat3Label}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-cream/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-2 bg-cream rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
